/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type ExperienceNameType } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { WarningIllustration } from '@atlassian/conversation-assistant-ui-components/ui/WarningIllustration';

import { errorBoundaryMessages } from './messages';

const styles = cssMap({
	button: {
		backgroundColor: token('color.background.neutral.subtle'),
		border: 'none',
		display: 'inline',
		color: token('color.link'),
		cursor: 'pointer',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
	},

	wrapper: {
		height: '100%',
		paddingInline: token('space.400'),
	},

	marginTop: {
		marginTop: token('space.300'),
	},
});

type ErrorViewProps = {
	error: Error;
	experienceName: ExperienceNameType;
	onClose: () => void;
};

const ErrorView = ({ error, experienceName, onClose }: ErrorViewProps) => {
	const { formatMessage } = useIntl();

	const getDescriptionRender = () => {
		return (
			<Pressable xcss={styles.button} onClick={onClose}>
				{formatMessage(errorBoundaryMessages.description)}
			</Pressable>
		);
	};

	return (
		<Flex
			columnGap="space.100"
			alignItems="center"
			justifyContent="center"
			direction="column"
			xcss={styles.wrapper}
			testId="ai-mate-generic-error"
		>
			<WarningIllustration />
			<Stack xcss={styles.marginTop} space="space.150" alignInline="center">
				<Heading size="medium">{formatMessage(errorBoundaryMessages.title)}</Heading>
				<Text as="p">{getDescriptionRender()}</Text>
			</Stack>
		</Flex>
	);
};

type ErrorBoundaryState = {
	error?: Error;
};

// eslint-disable-next-line @repo/internal/react/no-class-components
export class ErrorBoundary extends React.Component<
	React.PropsWithChildren<{
		experienceName: ExperienceNameType;
		rethrowErrors?: boolean;
		onClose: () => void;
	}>,
	ErrorBoundaryState
> {
	static displayName = 'RovoNavigationErrorBoundary';

	public state: ErrorBoundaryState = {};

	componentDidCatch(error: Error): void {
		this.setState({ error });
		if (this.props.rethrowErrors) {
			throw error;
		}
	}

	resetError = (): void => {
		this.setState({ error: undefined });
	};

	render(): string | number | boolean | JSX.Element | Iterable<React.ReactNode> | null | undefined {
		const { error } = this.state;
		const { children, experienceName, onClose } = this.props;

		if (error) {
			return <ErrorView error={error} experienceName={experienceName} onClose={onClose} />;
		}

		return children;
	}
}
